import { useEffect, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { Alert, Skeleton } from '@vtex/shoreline'

import Panel from '~/components/Panel'
import { formatMoney, formatNumberWithDots } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import {
  getInvestmentEstimateService,
  getInvestmentEstimateServiceKey
} from '~/modules/retailMedia/services/campaign/investment-estimate'

import * as S from './styles'

// Mirrored from the domain (FR-028) -- not read from the catalog, so it
// doesn't depend on the reference query having resolved yet.
//
// Named per campaign, not per media (review comment, 2026-09-09): Publya's
// own "per media" wording refers to their order form, where a single order
// can combine multiple platforms (Meta + DV360) as one "mídia" -- our
// campaigns are always single-channel (one platformId per campaign), so the
// R$5.000 floor Publya documents as "per media" is, structurally, our
// per-campaign minimum. The old name implied a per-creative/per-banner
// multiplier that never existed.
export const MIN_INVESTMENT_PER_CAMPAIGN = 5000

const DEBOUNCE_MS = 500

type TranslateFn = ReturnType<typeof useAPPTranslation>['t']

// Same rationale as AudienceTargeting/Interests.tsx's helper of the same
// name: i18next's generated key type is a closed literal union, which
// can't type a key held in a plain-string field of METRIC_ROWS below.
const translateDynamicKey = (t: TranslateFn, key: string): string =>
  (t as unknown as (key: string) => string)(key)

const formatMetricValue = (
  value: number | undefined,
  format?: (value: number) => string
): string | number => {
  // Publya's real API returns 0 (not null/omitted) for a metric that
  // doesn't apply -- confirmed 2026-09-04 testing against the live
  // integration -- so 0 is treated the same as "not defined" (falls back
  // to '—'), same as InvestmentObjective's reference pills.
  if (!value) return '—'
  if (format) return format(value)

  return value
}

// Global mirror of CampaignChannel (declared in constants/campaignChannel.ts)
// as a plain string-literal union, not the enum -- matches how
// useCampaignForm() exposes `selectedChannel` (hooks/types.d.ts).
type OffsiteChannel = CampaignChannelValue

type InvestmentProjectionSnapshot = InvestmentEstimate & {
  channel: OffsiteChannel
  objective: InvestmentObjectiveType
  budget: number
}

type ProjectionsProps = {
  channel: OffsiteChannel
  objective: InvestmentObjectiveType | null
  budget: number
  onChange?: (snapshot: InvestmentProjectionSnapshot | null) => void
}

type MetricRow = {
  key: keyof InvestmentEstimate
  labelKey: string
  format?: (value: number) => string
}

// No aggregation formula is computed here, this is a straight readout of
// the simulation response (FR-029).
//
// Only the three headline projections (FR-029: "impressões, cliques e
// alcance") render, as bordered stat cards matching the prototype's
// `dv360-metrics` (ads-design-prototypes, apps/offsite/v2.html) -- reference
// screenshots 2026-09-01. The averages (avgCpm/avgCpc/avgCtr/avgFrequency)
// dropped from the render entirely: the prototype has no element for them in
// this block, and they're the same numbers already shown as reference pills
// on the objective card above (InvestmentObjective) -- still persisted in
// full via onChange/settings.projections (FR-038), just not redrawn here.
//
// All three cards always render, even when the response doesn't define a
// given field (e.g. meta/conversion has no clicks/reach, only impressions)
// -- the undefined ones show "—" instead of being omitted, matching the
// prototype's own placeholder default (reference screenshot 2026-09-01,
// author decision superseding the earlier FR-037 reading of "omitted, never
// zeroed").
const METRIC_ROWS: MetricRow[] = [
  {
    key: 'impressions',
    labelKey: 'rm:campaign.create.Projections.impressions.label',
    format: formatNumberWithDots
  },
  {
    key: 'reach',
    labelKey: 'rm:campaign.create.Projections.reach.label',
    format: formatNumberWithDots
  },
  {
    key: 'clicks',
    labelKey: 'rm:campaign.create.Projections.clicks.label',
    format: formatNumberWithDots
  }
]

const Projections: React.FC<ProjectionsProps> = ({
  channel,
  objective,
  budget,
  onChange
}) => {
  const { t } = useAPPTranslation()
  const [debouncedBudget, setDebouncedBudget] = useState(budget)

  // Debounce (contracts/publya-investment-integration.md, "Risco
  // registrado") -- avoids a Publya call on every keystroke.
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedBudget(budget), DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [budget])

  const isBelowMinimum =
    !!debouncedBudget && debouncedBudget < MIN_INVESTMENT_PER_CAMPAIGN

  // useQuery (not useMutation) so switching inputs quickly -- e.g. the
  // operator raising the budget again right after a Publya outage -- can't
  // leave a stale, slower response overwriting a newer one: each distinct
  // (channel, objective, debouncedBudget) tuple gets its own cache entry,
  // and react-query itself ignores a resolved fetch for a queryKey that
  // isn't the current one anymore. Same pattern as the sibling
  // InvestmentObjective's useReferenceQuery (Copilot review, 2026-09-04).
  const isEligible =
    !!channel && !!objective && !!debouncedBudget && !isBelowMinimum

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: [
      getInvestmentEstimateServiceKey,
      channel,
      objective,
      debouncedBudget
    ],
    queryFn: async () => {
      // Narrows objective (InvestmentObjectiveType | null) to non-null here
      // too, in depth: `enabled` below already guards the query from ever
      // running without one, but that guard living elsewhere gave the
      // compiler no way to catch it if it ever changed.
      if (!objective) {
        throw new Error('Projections: query called without an objective')
      }

      const response = await getInvestmentEstimateService({
        channel,
        objective,
        budget: debouncedBudget
      })
      return response.data
    },
    enabled: isEligible,
    // Same fix as InvestmentObjective's useReferenceQuery (review comment,
    // 2026-09-09): the app-wide QueryClient has no defaultOptions, so
    // without this, switching tabs and back re-triggers this Publya call
    // even though nothing eligible changed -- rewriting `capturedAt` on the
    // lifted snapshot (FR-038) to the refetch's timestamp instead of the
    // original simulation's. A real input change already gets a fresh
    // queryKey, so staleTime: Infinity only stops same-key refetches, not
    // real updates. (retry policy deliberately left at the QueryClient
    // default -- that's the BFF/domain's call, not this layer's, per the
    // same review thread.)
    staleTime: Infinity,
    refetchOnWindowFocus: false
  })

  useEffect(() => {
    if (!isEligible) {
      onChange?.(null)
      return
    }

    if (error) {
      onChange?.(null)
      return
    }

    // Eligible but not loaded yet -- either the initial fetch, or a new
    // eligible queryKey (channel/objective/budget changed) still loading.
    // Clearing here too (not just on error) keeps the lifted snapshot from
    // ever describing a combination that's no longer on screen (Copilot
    // review, 2026-09-04).
    if (!data) {
      onChange?.(null)
      return
    }

    onChange?.({ ...data, channel, objective, budget: debouncedBudget })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isEligible])

  // Investment below the R$5.000/campaign minimum is validated inline, from
  // the mirrored constant, before ever calling the simulation (FR-028) --
  // no round-trip to the API for this case.
  if (isBelowMinimum) {
    return (
      <Alert variant="warning">
        {t('rm:campaign.create.Projections.belowMinimum', {
          minInvestment: formatMoney(MIN_INVESTMENT_PER_CAMPAIGN)
        })}
      </Alert>
    )
  }

  if (!channel || !objective || (!debouncedBudget && !budget)) return null

  // Publya failure or timeout blocks the investment step -- no fallback,
  // no approximate number (FR-030d). Only after the typed budget has
  // settled: a mid-debounce change must not flash the previous error.
  if (error && budget === debouncedBudget) {
    return (
      <Alert variant="critical">
        {t('rm:campaign.create.Projections.error.unavailable')}
      </Alert>
    )
  }

  const isEstimatePending =
    budget !== debouncedBudget ||
    isLoading ||
    isFetching ||
    (isEligible && !data)

  return (
    <Panel title={t('rm:campaign.create.Projections.title')}>
      <S.MetricsGrid>
        {METRIC_ROWS.map(row => {
          const value = data?.[row.key]

          return (
            <S.MetricCard key={row.key}>
              <S.MetricLabel>
                {translateDynamicKey(t, row.labelKey)}
              </S.MetricLabel>
              <S.MetricValue>
                {isEstimatePending ? (
                  <Skeleton width="88px" height="20px" />
                ) : (
                  formatMetricValue(value as number | undefined, row.format)
                )}
              </S.MetricValue>
            </S.MetricCard>
          )
        })}
      </S.MetricsGrid>

      <p className="text-muted small mb-2">
        {t('rm:campaign.create.Projections.estimateNote')}
      </p>
    </Panel>
  )
}

export default Projections
